import {
  calcLevelByExperience,
  getExperienceProgress,
} from "@/utils/experience";
import * as React from "react";
import { useState, useEffect, useMemo } from "react";

interface QuestLevelProgressBarProps {
  rank: number | null;
  currentExperience: number;
  hasLeveledUp: boolean;
  xpGain: number;
  onReach100?: () => void;
  onClickRank?: () => void;
}

const QuestLevelProgressBar = ({
  rank,
  currentExperience,
  xpGain,
  onReach100,
  onClickRank,
}: QuestLevelProgressBarProps) => {
  const [animatedYellowProgress, setAnimatedYellowProgress] = useState(0);
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const animationFrameRef = React.useRef<number | null>(null);
  const hasReached100Ref = React.useRef<boolean>(false);
  const onReach100Ref = React.useRef(onReach100);

  // Update callback ref when it changes
  React.useEffect(() => {
    onReach100Ref.current = onReach100;
  }, [onReach100]);

  const { level, currentExp, expToCurrentLevel, expToNextLevel, remainingExp } =
    getExperienceProgress(currentExperience);

  const totalExpForLevel = expToNextLevel - expToCurrentLevel;
  const rawProgressPercentage =
    totalExpForLevel > 0 ? (remainingExp / totalExpForLevel) * 100 : 0;
  const EPSILON = 0.5;
  const shouldShow100Percent =
    totalExpForLevel > 0 && remainingExp >= totalExpForLevel - 1 - EPSILON;
  const progressPercentage = shouldShow100Percent
    ? 100
    : Math.min(Math.round(rawProgressPercentage), 100);

  const xpGainPercentage =
    totalExpForLevel > 0
      ? Math.min(
          Math.round((xpGain / totalExpForLevel) * 100),
          progressPercentage
        )
      : 0;

  const baseProgressPercentage = Math.max(
    progressPercentage - xpGainPercentage,
    0
  );

  const ANIMATION_SPEED = 20; // 20% per second

  const yellowDurationMs = useMemo(
    () => Math.max((xpGainPercentage / ANIMATION_SPEED) * 1000, 300),
    [xpGainPercentage]
  );

  useEffect(() => {
    // Reset animation state
    setAnimatedYellowProgress(0);
    setAnimatedPercentage(baseProgressPercentage);
    hasReached100Ref.current = false;

    const targetPercentage = Math.min(progressPercentage, 100);

    // Start yellow bar animation after a brief delay
    const timer1 = setTimeout(() => {
      setAnimatedYellowProgress(xpGainPercentage);
    }, 50);

    // Animate the percentage text
    const startTime = Date.now();
    let isCancelled = false;

    const animatePercentage = () => {
      if (isCancelled) return;

      const elapsed = Date.now() - startTime;
      const progressRatio = Math.min(elapsed / yellowDurationMs, 1);
      const currentPercentage = Math.round(
        baseProgressPercentage + progressRatio * xpGainPercentage
      );
      setAnimatedPercentage(currentPercentage);

      // Check if we've reached 100% and trigger callback
      if (
        currentPercentage >= 100 &&
        !hasReached100Ref.current &&
        progressPercentage >= 100
      ) {
        hasReached100Ref.current = true;
        if (onReach100Ref.current) {
          onReach100Ref.current();
        }
      }

      if (progressRatio < 1) {
        animationFrameRef.current = requestAnimationFrame(animatePercentage);
      } else {
        setAnimatedPercentage(targetPercentage);
        // Final check for 100% in case animation completes exactly at 100%
        if (targetPercentage >= 100 && !hasReached100Ref.current) {
          hasReached100Ref.current = true;
          if (onReach100Ref.current) {
            onReach100Ref.current();
          }
        }
        animationFrameRef.current = null;
      }
    };

    const timer2 = setTimeout(() => {
      if (!isCancelled) {
        animationFrameRef.current = requestAnimationFrame(animatePercentage);
      }
    }, 50);

    return () => {
      isCancelled = true;
      clearTimeout(timer1);
      clearTimeout(timer2);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
    // Only re-run when the actual input values change, not derived calculations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExperience, xpGain]);

  return (
    <div>
      <div className="flex justify-between w-full mb-[10px]">
        <div className="flex gap-[10px] text-[32px] text-primary-blue">
          <span>LVL:</span>
          <span>{level}</span>
        </div>
        <div
          className="flex gap-[10px] text-[16px] text-[#999999] items-end mb-[10px] border-b border-[#999999] cursor-pointer hover:text-primary-blue hover:border-primary-blue transition-colors duration-200"
          onClick={onClickRank}
        >
          <span>rank:</span>
          <span>{rank ?? "No rank"}</span>
        </div>
      </div>
      <div className="bg-black w-full pt-[50px]">
        <div className="relative mb-2">
          <div className="w-full h-2 bg-gray-800 flex flex-start">
            <div
              className="h-full bg-primary-blue"
              style={{
                width: `${baseProgressPercentage}%`,
              }}
            />
            <div
              className="relative h-full bg-primary-orange transition-all ease-out"
              style={{
                width: `${animatedYellowProgress}%`,
                transitionDuration: `${yellowDurationMs}ms`,
              }}
            >
              <div className="absolute  right-[-2px] top-[-5px] w-[2px] h-5 bg-primary-orange"></div>
            </div>
          </div>
          <div
            className="absolute top-[-43px]"
            style={{
              left: `${Math.max(0, Math.min(baseProgressPercentage + animatedYellowProgress - 4, 94))}%`,
            }}
          >
            <span className="text-white text-[20px]">
              {animatedPercentage}%
            </span>
          </div>
        </div>
        <div className="flex justify-between items-center mt-4 font-silkscreen">
          <div className="text-primary-light-gray text-[16px]">0 XP</div>
          {xpGain > 0 && (
            <div className="relative">
              <span className="text-primary-orange text-[24px]">
                +{xpGain} XP!
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestLevelProgressBar;
